import React, { useState, useEffect, cloneElement, isValidElement, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Loader2 } from "lucide-react";
// Import auth utilities from shared lib to avoid circular dependencies
// NOTE: Do NOT re-export these - it causes TDZ errors. All components should import from @/lib/api
import { AuthContext, useAuth, apiClient, API } from "@/lib/api";
import { ProfileProvider, useProfile } from "@/lib/ProfileContext";
import { GuidanceProvider } from "@/lib/GuidanceContext";

// Pages (static imports for fast-loading pages)
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import CreateContentPage from "@/pages/CreateContentPage";
import PostsPage from "@/pages/PostsPage";
import CalendarPage from "@/pages/CalendarPageV2";
import SettingsPage from "@/pages/SettingsPage";
// AutomationPage removed - Auto-Reply and Engagement Bots are admin-only features
import PricingPage from "@/pages/PricingPage";
import AdminPage from "@/pages/AdminPage";
import ViralSimulatorPage from "@/pages/ViralSimulatorPage";
import FeedbackPage from "@/pages/FeedbackPage";
import ContentRadarPage from "@/pages/ContentRadarPage";
import QuoteAcrossPage from "@/pages/QuoteAcrossPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import ContentIdeasPage from "@/pages/ContentIdeasPage";
import GroupFinderPage from "@/pages/GroupFinderPage";
import SwipeApprovePage from "@/pages/SwipeApprovePage";
import TikTokCallbackPage from "@/pages/TikTokCallbackPage";
import MockupPage from "@/pages/MockupPage";
import RewardsCenterPage from "@/pages/RewardsCenterPage";
import AutoReplyPage from "@/pages/AutoReplyPage";
// B2B Portals
import PartnerPortal from "@/pages/PartnerPortal";
import EnterprisePortal from "@/pages/EnterprisePortal";
import MerchantPortal from "@/pages/MerchantPortal";
import MerchantLogin from "@/pages/MerchantLogin";
import AdminResetPasswordPage from "@/pages/AdminResetPasswordPage";
// VideoGenerationPage removed - video creation now handled through Create page
import ViraChatbot from "@/components/ViraChatbot";
import CookieConsent from "@/components/CookieConsent";
import PushNotificationPrompt from "@/components/PushNotificationPrompt";
import ReviewPrompt from "@/components/ReviewPrompt";
import TikTokApprovalListener from "@/components/TikTokApprovalListener";

// Auth Provider (uses AuthContext from lib/api.js)
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("viraly_token");
    const savedUser = localStorage.getItem("viraly_user");
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      apiClient.get("/auth/me")
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("viraly_user", JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem("viraly_token");
          localStorage.removeItem("viraly_user");
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await apiClient.post("/auth/login", { email, password });
    localStorage.setItem("viraly_token", res.data.access_token);
    localStorage.setItem("viraly_user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    
    // Check for PWA install prompt (after 2nd login)
    const loginCount = res.data.user.login_count || 0;
    if (loginCount >= 2 && window.deferredPrompt) {
      setTimeout(() => {
        toast("Install Viraly App", {
          description: "Get the best experience with our mobile app!",
          action: {
            label: "Install",
            onClick: () => {
              window.deferredPrompt.prompt();
            }
          }
        });
      }, 2000);
    }
    
    return res.data;
  };

  const register = async (name, email, password, country = null, province = null, postalCode = null, referralCode = null) => {
    const res = await apiClient.post("/auth/register", { 
      name, 
      email, 
      password,
      country,
      province,
      postal_code: postalCode,
      referred_by: referralCode || localStorage.getItem("viraly_referral") || null
    });
    localStorage.setItem("viraly_token", res.data.access_token);
    localStorage.setItem("viraly_user", JSON.stringify(res.data.user));
    // Mark as new registration - triggers cookie consent and push prompt
    localStorage.setItem("viraly_just_registered", "true");
    // Clear referral code from storage
    localStorage.removeItem("viraly_referral");
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("viraly_token");
    localStorage.removeItem("viraly_user");
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("viraly_user", JSON.stringify(updatedUser));
  };

  const refreshUser = async () => {
    try {
      const res = await apiClient.get("/auth/me");
      setUser(res.data);
      localStorage.setItem("viraly_user", JSON.stringify(res.data));
      return res.data;
    } catch (error) {
      console.error("Failed to refresh user:", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, refreshUser }}>
      <ProfileProvider user={user}>
        {children}
      </ProfileProvider>
    </AuthContext.Provider>
  );
};

// Protected Route - Forces remount on route change
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-viraly-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Render children inside a keyed Fragment to force remount
  return <React.Fragment key={location.pathname}>{children}</React.Fragment>;
};

// PWA Install Prompt Handler
const usePWAInstall = () => {
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
    };
    
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
};

function App() {
  usePWAInstall();

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen bg-viraly-dark">
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              style: {
                background: "#16213E",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#EAEAEA"
              }
            }}
          />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/admin-reset-password" element={<AdminResetPasswordPage />} />
            <Route path="/login" element={<Navigate to="/auth" replace />} />
            <Route path="/signin" element={<Navigate to="/auth" replace />} />
            <Route path="/signup" element={<Navigate to="/auth" replace />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/create" 
              element={
                <ProtectedRoute>
                  <CreateContentPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/posts" 
              element={
                <ProtectedRoute>
                  <PostsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/calendar" 
              element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />
            {/* Automation route removed - admin-only feature */}
            <Route 
              path="/viral-simulator" 
              element={
                <ProtectedRoute>
                  <ViralSimulatorPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/feedback" 
              element={
                <ProtectedRoute>
                  <FeedbackPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/radar" 
              element={
                <ProtectedRoute>
                  <ContentRadarPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/quote-across" 
              element={
                <ProtectedRoute>
                  <QuoteAcrossPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ideas" 
              element={
                <ProtectedRoute>
                  <ContentIdeasPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/groups" 
              element={
                <ProtectedRoute>
                  <GroupFinderPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/swipe-approve" 
              element={
                <ProtectedRoute>
                  <SwipeApprovePage />
                </ProtectedRoute>
              } 
            />
            {/* Video route removed - video creation now handled through Create page */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/mockup" element={<MockupPage />} />
            <Route 
              path="/rewards" 
              element={
                <ProtectedRoute>
                  <RewardsCenterPage />
                </ProtectedRoute>
              } 
            />
            {/* Auto-Reply moved to Admin Panel - route kept for backwards compatibility redirect */}
            <Route 
              path="/auto-reply" 
              element={
                <ProtectedRoute>
                  <div className="min-h-screen bg-viraly-dark flex items-center justify-center">
                    <div className="text-center p-8">
                      <h1 className="text-2xl font-bold text-white mb-4">Auto-Reply Bot Moved</h1>
                      <p className="text-viraly-muted mb-4">The Auto-Reply Bot is now in the Admin Panel.</p>
                      <p className="text-sm text-viraly-muted">Access it at <code className="bg-white/10 px-2 py-1 rounded">/bajrangbali</code> → Auto-Reply Bot tab</p>
                    </div>
                  </div>
                </ProtectedRoute>
              } 
            />
            {/* OAuth Callback Routes */}
            <Route 
              path="/oauth/tiktok/callback" 
              element={
                <ProtectedRoute>
                  <TikTokCallbackPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/api/oauth/tiktok/callback" 
              element={
                <ProtectedRoute>
                  <TikTokCallbackPage />
                </ProtectedRoute>
              } 
            />
            <Route path="/bajrangbali/*" element={<AdminPage />} />
            
            {/* B2B Enterprise Portals - No auth required (they have their own login) */}
            <Route path="/partners" element={<PartnerPortal />} />
            <Route path="/enterprise/:slug" element={<EnterprisePortal />} />
            <Route path="/merchant" element={<MerchantLogin />} />
            <Route path="/m/:id" element={<MerchantPortal />} />
          </Routes>
          {/* Global Vira Chatbot - available everywhere */}
          <ViraChatbot />
          {/* TikTok Approval Listener - notifies when posts need TikTok approval */}
          <TikTokApprovalListener />
          {/* Review Prompt - shows after 3rd successful post */}
          <AuthenticatedReviewPrompt />
          {/* GDPR/PIPEDA Cookie Consent Banner */}
          <CookieConsent />
          {/* Push Notification Prompt - shows for authenticated users */}
          <AuthenticatedPushPrompt />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Wrapper component that only shows review prompt for logged-in users
function AuthenticatedReviewPrompt() {
  const { user } = useAuth();
  if (!user) return null;
  return <ReviewPrompt />;
}

// Wrapper component that only shows push prompt for logged-in users
function AuthenticatedPushPrompt() {
  const { user } = useAuth();
  
  // Only show for authenticated users
  if (!user) return null;
  
  return <PushNotificationPrompt />;
}

export default App;
